import type { CSSProperties } from 'react';
import axios from 'axios';
import { abbreviateLocation, fetchAbbreviations, getAllLocationAbbreviations } from './abbreviations';

/**
 * Fargeoppslag for steder på offentlige lenker (/kp, /sp, /pl) — samme regler som bookinglisten/Ukesplanen (useColorCoding.getLocationColor):
 * eksakt forkortelse → via forkortelseskartet (fullt navn/adresse/variasjon) → forkortelse som delstreng.
 */
export function makeLocationColorResolver(colors: Record<string, string> | undefined, shortcuts?: Record<string, string>) {
  const map = colors || {};
  return (loc: string | undefined | null): string | null => {
    if (!loc) return null;
    const upper = String(loc).toUpperCase().trim();
    if (map[upper]) return map[upper];
    if (shortcuts) {
      const abbr = abbreviateLocation(loc, shortcuts);
      if (abbr && map[String(abbr).toUpperCase()]) return map[String(abbr).toUpperCase()];
    }
    for (const [shortcut, color] of Object.entries(map)) {
      if (color && upper.includes(shortcut.toUpperCase())) return color;
    }
    return null;
  };
}

/** Inline-stil for et fargekodet sted (lys bakgrunn + venstre kant i stedets farge), som LocationCell i Ukesplanen. */
export const locationChipStyle = (color: string | null, borderWidth = '3px'): CSSProperties | undefined =>
  color ? { backgroundColor: `${color}30`, borderLeft: `${borderWidth} solid ${color}` } : undefined;

export type LocationColorFn = (loc: string | undefined | null) => string | null;

export const hexToRgb = (hex: string): [number, number, number] | null => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** Samme lyse tone som skjermens `${farge}30` (alpha ≈ 0,19) som ugjennomsiktig RGB — for PDF/Excel-fyll. */
export const tintRgb = (hex: string, alpha = 0.19): [number, number, number] | null => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return rgb.map((c) => Math.round(255 - (255 - c) * alpha)) as [number, number, number];
};

export const rgbToHex = ([r, g, b]: [number, number, number]) => `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;

let defaultResolverCache: { at: number; p: Promise<LocationColorFn> } | null = null;
const DEFAULT_RESOLVER_TTL_MS = 60000;

/** Innlogget kontekst: stedsfarger fra /api/colors/all + forkortelseskart (cachet 60 s) — brukes av eksport når kalleren ikke har fargeoppslag for hånden. */
export const getDefaultLocationColorResolver = (): Promise<LocationColorFn> => {
  const now = Date.now();
  if (!defaultResolverCache || now - defaultResolverCache.at > DEFAULT_RESOLVER_TTL_MS) {
    const p = Promise.all([
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/colors/all`).then((res) => res.data?.locations || {}),
      fetchAbbreviations().then(() => getAllLocationAbbreviations()).catch(() => undefined),
    ])
      .then(([colors, shortcuts]) => makeLocationColorResolver(colors, shortcuts))
      .catch(() => { defaultResolverCache = null; return () => null; });
    defaultResolverCache = { at: now, p };
  }
  return defaultResolverCache.p;
};
